/**
 * useProposalRealtime - Tempo real do modal de detalhes da proposta
 *
 * Subscreve Postgres Changes nas tabelas que alimentam a coluna lateral do
 * modal (engajamento + histórico/eventos) filtradas pela proposta aberta:
 *   - crm_proposal_events        (visualizações do link público, PDFs, envios)
 *   - crm_proposal_status_history(transições de situação)
 *   - crm_proposals              (mudanças na própria proposta, ex.: PDF pronto)
 *
 * Ao receber qualquer evento, invalida a query `deal-proposal-details`, que é a
 * única fonte de dados do modal (RPC get_proposal_details).
 *
 * Observações de implementação:
 *  - Canal criado dentro de useEffect com removeChannel no cleanup (evita leak);
 *  - Nome de canal único por montagem (mesmo padrão de useDealActivitiesRealtime),
 *    para não colidir com canais cacheados ao reabrir o modal em sequência.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.0.0 - Criação (tempo real em Engajamento e Histórico)
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger dedicado (respeita o sistema de logging condicional do projeto)
const logger = createLogger('ProposalRealtime');

/**
 * Ativa o tempo real para a proposta informada.
 *
 * @param proposalId - proposta aberta (null/undefined = não subscreve)
 */
export function useProposalRealtime(proposalId: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Sem proposta aberta: nenhum canal é criado
    if (!proposalId) return;

    const channelName = `proposal-rt-${proposalId}-${crypto.randomUUID()}`;
    logger.debug('Iniciando Realtime da proposta', { proposalId });

    /** Invalida os detalhes do modal (RPC única) */
    const invalidate = (source: string) => {
      logger.debug('Realtime: alteração recebida', { source, proposalId });
      queryClient.invalidateQueries({ queryKey: ['deal-proposal-details'] });
    };

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_proposal_events',
          filter: `proposal_id=eq.${proposalId}`,
        },
        () => invalidate('crm_proposal_events'),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_proposal_status_history',
          filter: `proposal_id=eq.${proposalId}`,
        },
        () => invalidate('crm_proposal_status_history'),
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crm_proposals',
          filter: `id=eq.${proposalId}`,
        },
        () => invalidate('crm_proposals'),
      )
      .subscribe((status) => {
        logger.debug('Status da subscription da proposta', { status });
      });

    // Cleanup ao fechar o modal ou trocar de proposta
    return () => {
      logger.debug('Removendo Realtime da proposta', { proposalId });
      supabase.removeChannel(channel);
    };
  }, [proposalId, queryClient]);
}
